import React, { useState, useEffect } from 'react';
import { Play, VideoOff, Film, Wand2 } from 'lucide-react';
import { StudioTransportBar, PlaybackMode } from './StudioTransportBar';
import { SoulStreamLoader, BakerStageId } from './SoulStreamLoader';

interface StudioVideoStageProps {
  videoContainerRef: React.RefObject<HTMLDivElement | null>;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  vocalAudioRef: React.RefObject<HTMLAudioElement | null>;
  bgmAudioRef: React.RefObject<HTMLAudioElement | null>;
  takeAudioRef: React.RefObject<HTMLAudioElement | null>;
  takeAuditionAudioRef: React.RefObject<HTMLAudioElement | null>;
  origAuditionAudioRef: React.RefObject<HTMLAudioElement | null>;
  videoStreamSrc: string;
  vocalStemSrc: string;
  bgmStemSrc: string;
  posterUrl?: string;
  hasStems: boolean;
  hasClip?: boolean;
  isPlaying: boolean;
  countdown: number | null;
  isRecording: boolean;
  currentTime: number;
  duration: number;
  hasTakes: boolean;
  playbackMode: PlaybackMode;
  onTimeUpdate: () => void;
  onPlay: () => void;
  onPause: () => void;
  onWaiting: () => void;
  onPlaying: () => void;
  onEnded: () => void;
  onTogglePlay: () => void;
  onPlayRange: (paddingSec?: number) => void;
  onSeek: (seconds: number) => void;
  onSetPlaybackMode: (mode: PlaybackMode) => void;
  onTakeAuditionEnded: () => void;
  onOrigAuditionEnded: () => void;
  onOpenVault?: () => void;
  onBackToCapture?: () => void;
  showBakeOverlay: boolean;
  bakeStage: BakerStageId;
  clipTitle: string;
  transferDone: boolean;
  splitDone: boolean;
  hearDone: boolean;
  isLoadingDialogue?: boolean;
  bakeTimeoutError?: string | null;
  onRetryBake?: () => void;
  transferStatusText?: string;
  transferProgress?: number;
  onSkipHear?: () => void;
  formatTime: (secs: number) => string;
}

export const StudioVideoStage: React.FC<StudioVideoStageProps> = ({
  videoContainerRef,
  videoRef,
  vocalAudioRef,
  bgmAudioRef,
  takeAudioRef,
  takeAuditionAudioRef,
  origAuditionAudioRef,
  videoStreamSrc,
  vocalStemSrc,
  bgmStemSrc,
  posterUrl,
  hasStems,
  hasClip = true,
  isPlaying,
  countdown,
  isRecording,
  currentTime,
  duration,
  hasTakes,
  playbackMode,
  onTimeUpdate,
  onPlay,
  onPause,
  onWaiting,
  onPlaying,
  onEnded,
  onTogglePlay,
  onPlayRange,
  onSeek,
  onSetPlaybackMode,
  onTakeAuditionEnded,
  onOrigAuditionEnded,
  onOpenVault,
  onBackToCapture,
  showBakeOverlay,
  bakeStage,
  clipTitle,
  transferDone,
  splitDone,
  hearDone,
  isLoadingDialogue,
  transferStatusText,
  transferProgress,
  bakeTimeoutError,
  onRetryBake,
  onSkipHear,
  formatTime
}) => {
  const isUnloaded = !hasClip;

  // Fade the bake overlay out (then unmount) once everything is ready
  const [bakeOverlayHidden, setBakeOverlayHidden] = useState(false);
  useEffect(() => {
    if (showBakeOverlay) {
      setBakeOverlayHidden(false);
      return;
    }
    const t = setTimeout(() => setBakeOverlayHidden(true), 600);
    return () => clearTimeout(t);
  }, [showBakeOverlay]);

  return (
    <div
      ref={videoContainerRef}
      className="studio-video-container"
      style={{ cursor: isUnloaded ? 'default' : 'pointer' }}
    >
      {isUnloaded ? (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'radial-gradient(ellipse at center, rgba(30, 27, 75, 0.7) 0%, rgba(10, 15, 30, 0.98) 100%)',
            padding: '2rem',
            textAlign: 'center',
            gap: '12px',
            zIndex: 10
          }}
        >
          <div
            style={{
              width: '54px',
              height: '54px',
              borderRadius: '50%',
              background: 'rgba(139, 92, 246, 0.12)',
              border: '1px solid rgba(139, 92, 246, 0.35)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#c4b5fd',
              boxShadow: '0 0 24px rgba(139, 92, 246, 0.25)'
            }}
          >
            <VideoOff size={24} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={{ fontSize: '1.05rem', fontWeight: 800, color: '#f8fafc' }}>
              No Soul Bound to This Scene
            </span>
            <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', maxWidth: '360px', lineHeight: 1.5 }}>
              Open the SoulVault to choose a captured soul, or journey back to SoulCapture to weave a new one.
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', justifyContent: 'center', marginTop: '4px' }}>
            {onBackToCapture && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onBackToCapture();
                }}
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  color: '#e9d5ff',
                  border: '1px solid rgba(139, 92, 246, 0.4)',
                  borderRadius: '8px',
                  padding: '6px 14px',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <Wand2 size={13} color="#c4b5fd" />
                <span>Return to SoulCapture</span>
              </button>
            )}
            {onOpenVault && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenVault();
                }}
                style={{
                  background: 'linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '6px 14px',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: '0 4px 14px rgba(124, 58, 237, 0.35)'
                }}
              >
                <Film size={13} />
                <span>Open SoulVault</span>
              </button>
            )}
          </div>
        </div>
      ) : (
        <video
          ref={videoRef}
          src={videoStreamSrc || undefined}
          poster={posterUrl || undefined}
          playsInline
          muted={hasStems}
          onTimeUpdate={onTimeUpdate}
          onSeeked={onTimeUpdate}
          onPlay={onPlay}
          onPause={onPause}
          onWaiting={onWaiting}
          onPlaying={onPlaying}
          onEnded={onEnded}
          onClick={(e) => {
            e.stopPropagation();
            onTogglePlay();
          }}
          className="studio-video-element"
        />
      )}

      {/* Hidden Stems and Take Audio Elements for Web Audio pipeline */}
      <audio ref={vocalAudioRef} src={vocalStemSrc || undefined} preload="auto" crossOrigin="anonymous" />
      <audio ref={bgmAudioRef} src={bgmStemSrc || undefined} preload="auto" crossOrigin="anonymous" />
      <audio ref={takeAudioRef} preload="auto" crossOrigin="anonymous" />
      <audio ref={takeAuditionAudioRef} preload="auto" onEnded={onTakeAuditionEnded} />
      <audio
        ref={origAuditionAudioRef}
        preload="auto"
        crossOrigin="anonymous"
        onEnded={onOrigAuditionEnded}
      />

      {/* 3..2..1 Countdown Overlay */}
      {countdown !== null && (
        <div className="countdown-overlay">
          <span className="countdown-number">{countdown}</span>
          <span
            style={{
              fontSize: '0.85rem',
              fontWeight: 700,
              color: '#fca5a5',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              marginTop: '8px'
            }}
          >
            Get Ready to Record
          </span>
        </div>
      )}

      {/* Recording Active Indicator */}
      {isRecording && (
        <div
          style={{
            position: 'absolute',
            top: '12px',
            left: '12px',
            background: '#ef4444',
            color: '#fff',
            fontSize: '0.75rem',
            padding: '4px 12px',
            borderRadius: 'var(--radius-full)',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            zIndex: 30,
            boxShadow: '0 0 16px rgba(239, 68, 68, 0.7)'
          }}
        >
          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#fff', display: 'inline-block' }} />
          RECORDING ACTIVE
        </div>
      )}

      {/* Pending R2 Media Baking Overlay: SoulSync-themed 3-stage loader */}
      {!bakeOverlayHidden && !isUnloaded && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 35,
            opacity: showBakeOverlay ? 1 : 0,
            transition: 'opacity 600ms ease',
            pointerEvents: showBakeOverlay ? 'auto' : 'none'
          }}
        >
          <SoulStreamLoader
            stage={bakeStage}
            clipTitle={clipTitle}
            transferDone={transferDone}
            splitDone={splitDone}
            hearDone={hearDone}
            isLoadingDialogue={Boolean(isLoadingDialogue)}
            transferStatusText={transferStatusText}
            transferProgress={transferProgress}
            bakeTimeoutError={bakeTimeoutError}
            onRetryBake={onRetryBake}
            onBackToCapture={onBackToCapture}
            onSkipHear={onSkipHear}
          />
        </div>
      )}

      {/* Big Play Overlay if paused */}
      {!isPlaying && countdown === null && !showBakeOverlay && !isUnloaded && (
        <button
          type="button"
          onClick={onTogglePlay}
          style={{
            position: 'absolute',
            inset: 0,
            margin: 'auto',
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            background: 'rgba(139, 92, 246, 0.85)',
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(8px)',
            boxShadow: 'var(--shadow-glow-purple)',
            zIndex: 25,
            transition: 'transform 0.2s ease'
          }}
          aria-label="Play Video"
        >
          <Play size={28} fill="currentColor" style={{ marginLeft: '3px' }} />
        </button>
      )}

      {/* Transport Controls Bar */}
      <StudioTransportBar
        isPlaying={isPlaying}
        currentTime={currentTime}
        duration={duration}
        hasTakes={hasTakes}
        playbackMode={playbackMode}
        onTogglePlay={onTogglePlay}
        onPlayRange={onPlayRange}
        onSeek={onSeek}
        onSetPlaybackMode={onSetPlaybackMode}
        formatTime={formatTime}
      />
    </div>
  );
};
